import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { PARTNER } from "../data/mockContacts";

const AppContext = createContext(null);

const TX_KEY = "shwiip.transactions.v1";
const DRAFT_KEY = "shwiip.draft.v1";
const ACTIVE_TX_KEY = "shwiip.active_tx.v1";

const PARTNERS = ["Finshark", "Fasto", "Zimpler"];

const loadJSON = (k, fallback) => {
  try {
    const raw = localStorage.getItem(k);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const seedIfEmpty = () => {
  const existing = loadJSON(TX_KEY, null);
  if (existing && existing.length) return existing;
  const now = Date.now();
  const seed = [
    {
      id: "TX-" + (now - 86400000),
      reference: "SHW-8F2A3C71",
      recipient: { name: "John Martin", phone: "+46 70 123 4501", initials: "JM", color: "#FDE68A" },
      amount: 50,
      fee: 0.75,
      recipientGets: 49.25,
      currency: "SEK",
      status: "COMPLETED",
      partner: PARTNER.name,
      method: "Bank Transfer",
      createdAt: now - 86400000,
    },
    {
      id: "TX-" + (now - 3 * 86400000),
      reference: "SHW-1B84D902",
      recipient: { name: "Anna Lindqvist", phone: "+46 70 123 4502", initials: "AL", color: "#BFDBFE" },
      amount: 120,
      fee: 1.5,
      recipientGets: 118.5,
      currency: "SEK",
      status: "COMPLETED",
      partner: PARTNER.name,
      method: "Bank Transfer",
      createdAt: now - 3 * 86400000,
    },
    {
      id: "TX-" + (now - 5 * 86400000),
      reference: "SHW-44A10C29",
      recipient: { name: "Marco Rossi", phone: "+39 333 555 0103", initials: "MR", color: "#FBCFE8" },
      amount: 35,
      fee: 0.5,
      recipientGets: 34.5,
      currency: "SEK",
      status: "FAILED",
      partner: PARTNER.name,
      method: "Bank Transfer",
      createdAt: now - 5 * 86400000,
    },
  ];
  localStorage.setItem(TX_KEY, JSON.stringify(seed));
  return seed;
};

export const AppProvider = ({ children }) => {
  const [transactions, setTransactions] = useState(() => seedIfEmpty());
  const [draft, setDraft] = useState(() =>
    loadJSON(DRAFT_KEY, { recipient: null, amount: "", currency: "SEK" })
  );
  const [activeTx, setActiveTx] = useState(() => loadJSON(ACTIVE_TX_KEY, null));
  const initialBalance = 5000;

  const totalSent = transactions
    .filter((t) => t.status === "COMPLETED")
    .reduce((s, t) => s + t.amount, 0);
  const balance = initialBalance - totalSent;

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [draft]);

  useEffect(() => {
    localStorage.setItem(TX_KEY, JSON.stringify(transactions));
  }, [transactions]);

  useEffect(() => {
    if (activeTx) {
      localStorage.setItem(ACTIVE_TX_KEY, JSON.stringify(activeTx));
    } else {
      localStorage.removeItem(ACTIVE_TX_KEY);
    }
  }, [activeTx]);

  const updateDraft = useCallback((patch) => {
    setDraft((d) => ({ ...d, ...patch }));
  }, []);

  const resetDraft = useCallback(() => {
    setDraft({ recipient: null, amount: "", currency: "SEK" });
  }, []);

  const computeFee = useCallback((amount) => {
    const n = Number(amount) || 0;
    if (n <= 0) return 0;
    return Math.round(Math.max(0.5, n * 0.015) * 100) / 100;
  }, []);

  const createTransaction = useCallback(() => {
    const amt = Number(draft.amount) || 0;
    const fee = computeFee(amt);
    const id = "TX-" + Date.now();
    const reference = "SHW-" + Math.random().toString(16).slice(2, 10).toUpperCase();
    const partnerName = PARTNERS[Math.floor(Math.random() * PARTNERS.length)];
    const tx = {
      id,
      reference,
      recipient: draft.recipient,
      amount: amt,
      fee,
      recipientGets: Math.round((amt - fee) * 100) / 100,
      currency: draft.currency || "SEK",
      status: "INIT",
      partner: partnerName,
      method: "Bank Transfer",
      createdAt: Date.now(),
    };
    setActiveTx(tx);
    return tx;
  }, [draft, computeFee]);

  const updateActiveTx = useCallback((patch) => {
    setActiveTx((t) => (t ? { ...t, ...patch } : t));
  }, []);

  const persistTransaction = useCallback((tx) => {
    setTransactions((list) => {
      const existing = list.findIndex((x) => x.id === tx.id);
      if (existing >= 0) {
        const next = [...list];
        next[existing] = tx;
        return next;
      }
      return [tx, ...list];
    });
  }, []);

  const value = {
    transactions,
    draft,
    updateDraft,
    resetDraft,
    computeFee,
    activeTx,
    setActiveTx,
    createTransaction,
    updateActiveTx,
    persistTransaction,
    partner: PARTNER,
    initialBalance,
    balance,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
